import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import moment from 'moment';

import {
  IResumeDatabaseAccess,
  IResumeDatabaseListing,
  getResumeDatabaseErrorMessage,
  useAcceptResumeDatabaseTermsMutation,
} from '../../services/serviceResume';

const panelStyles = {
  borderRadius: 0,
  boxShadow: '6px 6px 0 var(--accent-soft)',
  mx: 'auto',
  my: 4,
  p: { xs: 3, md: 4.5 },
  maxWidth: 820,
} as const;

const monoLabelStyles = {
  color: 'var(--accent-text)',
  fontSize: '0.8rem',
  fontWeight: 700,
} as const;

export const formatResumeDate = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
};

// The prohibitions the current terms version must state plainly. Copy lives here
// so the acceptance panel and the introduction dialog cannot drift apart.
export const RESUME_DATABASE_TERMS = [
  'Use candidate profiles only to recruit for the specific job listing you select, not for marketing, sales outreach, research, or any unrelated purpose.',
  'Do not scrape, bulk-extract, copy, export, or store the Talent Network in another system.',
  'Do not redistribute, resell, or share candidate information outside your hiring team.',
  'Do not send unsolicited marketing or spam to a candidate who shares their details.',
  'Do not share your BevNET sign-in with anyone else. Every search, profile view, and introduction is recorded against your account.',
];

export const ResumeDatabaseTerms = ({ access }: { access: IResumeDatabaseAccess }) => {
  const [agreed, setAgreed] = useState(false);
  const [acceptTerms, acceptResult] = useAcceptResumeDatabaseTermsMutation();
  const errorMessage = acceptResult.error
    ? getResumeDatabaseErrorMessage(acceptResult.error)
      || 'We could not record your acceptance. Please try again.'
    : null;

  return (
    <Paper variant="outlined" sx={panelStyles}>
      <GavelOutlinedIcon sx={{ color: 'var(--accent-text)', fontSize: 44 }} />
      <Typography sx={{ ...monoLabelStyles, mt: 1 }}>
        Resume Database terms
        {access.currentTermsVersion ? ` · version ${access.currentTermsVersion}` : ''}
      </Typography>
      <Typography component="h2" variant="h5" sx={{ mb: 1.5, mt: 0.8 }}>
        Accept the current terms to search the Talent Network
      </Typography>
      <Typography color="text.secondary">
        Candidates join the Talent Network on the understanding that employers use it to recruit.
        These rules keep it that way.
      </Typography>
      <Box component="ul" sx={{ mt: 2, pl: 2.5 }}>
        {RESUME_DATABASE_TERMS.map((rule) => (
          <Typography key={rule} component="li" sx={{ mb: 1 }}>{rule}</Typography>
        ))}
      </Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Resume Database activity is logged. BevNET may pause your access if these rules are broken,
        or if candidates report your outreach.
      </Alert>
      {access.acceptedTermsVersion && access.acceptedTermsVersion !== access.currentTermsVersion ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          These terms have been updated since you accepted version {access.acceptedTermsVersion}.
          Accept the current version to continue.
        </Alert>
      ) : null}
      {errorMessage ? <Alert severity="error" sx={{ mb: 2 }}>{errorMessage}</Alert> : null}
      <FormControlLabel
        control={(
          <Checkbox
            checked={agreed}
            onChange={(_event, checked) => setAgreed(checked)}
            inputProps={{ 'aria-label': 'I have read and agree to the Resume Database terms' }}
          />
        )}
        label="I have read and agree to these terms."
      />
      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          disabled={!agreed || acceptResult.isLoading}
          onClick={() => acceptTerms()}
        >
          Accept and continue
        </Button>
      </Box>
    </Paper>
  );
};

export const ResumeDatabaseSuspended = ({ access }: { access: IResumeDatabaseAccess }) => {
  const suspendedOn = formatResumeDate(access.suspendedDate);

  return (
    <Paper variant="outlined" sx={panelStyles}>
      <PauseCircleOutlineIcon sx={{ color: 'var(--accent-text)', fontSize: 48 }} />
      <Typography component="h2" variant="h5" sx={{ mb: 1.5, mt: 1 }}>
        Resume Database access is paused
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        A BevNET administrator paused Resume Database access for this account
        {suspendedOn ? ` on ${suspendedOn}` : ''}. Searching, viewing candidate profiles, and
        requesting introductions are unavailable until it is restored. Your job listings and
        applicants are unaffected.
      </Typography>
      {access.suspensionReason ? (
        <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
          <strong>Reason given:</strong> {access.suspensionReason}
        </Alert>
      ) : null}
      <Typography color="text.secondary">
        To have this reviewed, use the <strong>Questions?</strong> button at the top of the page to
        contact BevNET support.
      </Typography>
    </Paper>
  );
};

export const ResumeDatabaseHistoricalOnly = ({
  onShowRequests,
}: {
  onShowRequests?: () => void;
}) => (
  <Paper variant="outlined" sx={panelStyles}>
    <LockOutlinedIcon sx={{ color: 'var(--accent-text)', fontSize: 48 }} />
    <Typography component="h2" variant="h5" sx={{ mb: 1.5, mt: 1 }}>
      Search is paused until you have an active Featured listing
    </Typography>
    <Typography color="text.secondary" sx={{ mb: 2 }}>
      You can still open the candidates who already approved your introduction requests, and their
      contact details stay available to you. Searching the Talent Network and requesting new
      introductions need an active Featured job listing.
    </Typography>
    {onShowRequests ? (
      <Button variant="contained" onClick={onShowRequests}>View my introduction requests</Button>
    ) : null}
  </Paper>
);

// Remaining introductions for each eligible listing, shown beside the request
// action. The allowance is shared by every mapped owner of the same listing.
export const ListingAllowanceSummary = ({
  listings,
  compact,
}: {
  listings: IResumeDatabaseListing[];
  compact?: boolean;
}) => {
  if (!listings.length) return null;

  return (
    <Box
      sx={{
        backgroundColor: 'var(--bg-2)',
        border: '1px solid var(--line-soft)',
        mb: compact ? 0 : 3,
        p: compact ? 1.5 : 2,
      }}
    >
      <Typography sx={monoLabelStyles}>Introductions remaining</Typography>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {listings.map((listing) => {
          const resets = formatResumeDate(listing.windowResetDate);
          return (
            <Stack
              key={listing.classifiedId}
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography fontWeight={800}>{listing.title || `Listing ${listing.classifiedId}`}</Typography>
                {listing.introductionsRemaining <= 0 && resets ? (
                  <Typography color="text.secondary" variant="body2">
                    Allowance resets {resets}
                  </Typography>
                ) : resets ? (
                  <Typography color="text.secondary" variant="body2">
                    One slot frees {resets}
                  </Typography>
                ) : null}
              </Box>
              <Chip
                size="small"
                color={listing.introductionsRemaining > 0 ? 'success' : 'default'}
                label={`${listing.introductionsRemaining} of ${listing.introductionsAllowed} left`}
              />
            </Stack>
          );
        })}
      </Stack>
      <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mt: 1 }}>
        Each listing&rsquo;s allowance is shared by everyone on your team who manages that listing.
      </Typography>
    </Box>
  );
};
